import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { registerUser } from '../../core/store/actions/auth';import AuthService from '../../core/services/AuthService'; // Add this import
import { useHistory } from 'react-router-dom';

import "../../styles/Form.css";
import "../../styles/general.css";
import { Icon } from '@iconify/react/dist/iconify.js';
import CustomAlert from '../CustomAlert';

import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';

const RegisterForm2 = ({ getRecaptchaToken }) => {
    // Function to add a icon to error feedback bellow the input fields (a simple workaround)
    const withIcon = (message) => (
        <span style={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
            <Icon icon="f7:exclamationmark-circle-fill" style={{ marginRight: '5px', marginLeft: '24px' }} /> {message}
        </span>
    );

    const [step, setStep] = useState(1);
    const [registrationError, setRegistrationError] = useState(null); // Add this state
    const [isSubmitting, setIsSubmitting] = useState(false); // Add this state
    const [isCheckingEmail, setIsCheckingEmail] = useState(false); // Add this state
    const history = useHistory();
    // For CustomAlert
    const [alertInfo, setAlertInfo] = useState({
        show: false,
        type: '',
        message: ''
    });

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        firstLastName: '',
        nickname: '',
        birthDate: '',
        nationality: '',
        naturality: '',
        professionalStatus: 'Student',
        profilePicture: null,

        personalEmail: '',
        institutionalEmail: '',
        phone: '',
        address: '',

        emergencyName: '',
        emergencyRelationship: '',
        emergencyPhone: '',

        mobilityPlace: '',
        mobilityProgram: '',
        dateJoined: '',
        membershipType: 'Newbie',
        departments: [],
    });

    // New function to check email availability
    const checkEmailAvailability = async (email) => {
        try {
            setIsCheckingEmail(true);
            setRegistrationError(null);

            // console.log("🔍 Checking email availability:", email);

            await AuthService.checkEmailExists(email);

            // If we reach here, the email check passed (this shouldn't happen in a real check)
            return true;

        } catch (error) {
            // console.log("📧 Email check result:", error);

            // Check if the error is specifically about duplicate email
            if (error.response?.data?.exception === "Email already registered") {
                throw new Error("Email already registered");
            }

            // For other errors, re-throw them
            throw new Error(error.response?.data?.exception || "Email check failed");
        } finally {
            setIsCheckingEmail(false);
        }
    };

    const handleNext = async (values) => {
        setRegistrationError(null); // Clear error when moving to next step

        // If moving from step 1, check email availability first
        try {
            if (step === 1) {
                console.log("🔄 Checking email before proceeding...");
                await checkEmailAvailability(values.email);
            }
            setStep((prevStep) => prevStep + 1);
        } catch (error) {
            setRegistrationError(error.message); // Add this line to display the error
            // Don't proceed to next step when there's an error
        }
    };
    const handlePrev = () => {
        setRegistrationError(null); // Clear error when moving to previous step
        setStep((prevStep) => prevStep - 1);
    };

    const validationSchemas = [
        Yup.object({
            email: Yup.string()
                .email(withIcon('Invalid email'))
                .required(withIcon('Email is required')),
            password: Yup.string()
                .min(6, withIcon('Password must be at least 6 characters'))
                .required(withIcon('Password is required')),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], withIcon('Passwords must match'))
                .required(withIcon('Confirm Password is required')),
        }),
        Yup.object({
            profilePicture: Yup.mixed()
                .required(withIcon('Profile picture is required'))
                .test('fileSize', withIcon('File size is too large'), (value) => !value || value.size <= 10485760)
                .test('fileType', withIcon('File must be an image'), (value) => !value || value.type?.startsWith('image/')),
            fullName: Yup.string().required(withIcon('Required')),
            firstLastName: Yup.string().required(withIcon('Required')),
            nickname: Yup.string(),
            birthDate: Yup.date().required(withIcon('Required')),
            nationality: Yup.string().required(withIcon('Required')),
            naturality: Yup.string().required(withIcon('Required')),
            professionalStatus: Yup.string()
        }),
        Yup.object({
            personalEmail: Yup.string().email(withIcon('Invalid email format')),
            institutionalEmail: Yup.string().email(withIcon('Invalid email format')),
            phone: Yup.string().required(withIcon('Required')),
            address: Yup.string().required(withIcon('Required'))
        }),
        Yup.object({
            emergencyName: Yup.string().required(withIcon('Required')),
            emergencyRelationship: Yup.string().required(withIcon('Required')),
            emergencyPhone: Yup.string().required(withIcon('Required')),
        }),
        Yup.object({
            mobilityPlace: Yup.string(),
            mobilityProgram: Yup.string(),
            dateJoined: Yup.date().required(withIcon('Required')),
            membershipType: Yup.string(),
            departments: Yup.array().required(withIcon('Select a department'))
                .min(1, withIcon('Select a department'))
                .max(1, withIcon('You can only select one department')),

        }),
    ];

    const dispatch = useDispatch();

    const handleSubmit = async (values) => {
        try {
            console.log("🚀 Starting registration");
            setIsSubmitting(true);
            setRegistrationError(null);

            const { profilePicture, ...registrationData } = values;

            if (!getRecaptchaToken) {
                throw new Error(
                    'reCAPTCHA is not configured. Add REACT_APP_RECAPTCHA_SITE_KEY to web/.env and restart the web app.'
                );
            }

            const recaptchaToken = await getRecaptchaToken();

            const profile = await dispatch(registerUser({
                ...registrationData,
                recaptchaToken,
            }));

            if (!profile?._id) {
                setRegistrationError('Registration failed. Please try again.');
                setAlertInfo({
                    show: true,
                    type: 'error',
                    message: 'There was an error in the registration. Please try again. If the error persists, contact the IT Department.'
                });
                return;
            }

            if (!profilePicture) {
                throw new Error('Profile picture is required');
            }

            await AuthService.uploadProfilePicture(
                profile._id,
                profilePicture,
                profile.token
            );

            history.push('/waiting-for-hr');
        } catch (error) {
            console.error('❌ Registration failed:', error);

            // Extract error message
            let errorMessage = 'Registration failed';

            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error.message) {
                errorMessage = error.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.status === 403) {
                errorMessage = 'Permission denied while uploading your profile picture. Please log out, refresh the page, and try registering again.';
            } else if (error.response?.data?.exception) {
                errorMessage = error.response.data.exception;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            setRegistrationError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <>
        {/* Alert that shows upon event submission */}
        {alertInfo.show && (
            <CustomAlert
                type={alertInfo.type}
                message={alertInfo.message}
                autoDismiss={true}
                timeout={4000}
                onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
            />
        )}
        <Formik
            initialValues={formData}
            validationSchema={validationSchemas[step - 1]}
            onSubmit={async (values) => {
                setFormData(values);

                if (step < 5) {
                    handleNext(values);
                    //console.log(values);
                } else {
                    handleSubmit(values);
                }
            }}
        >
            {({ values, setFieldValue, touched, errors }) => (
                <Form style={{ margin: '100px 50px' }}>

                    <div className="title mb-3" style={{ textAlign: 'center' }}>Hello!</div>
                    {step === 1 && <Step1 formData={values} />}
                    {step === 2 && (
                        <Step2
                            values={values}
                            setFieldValue={setFieldValue}
                            touched={touched}
                            errors={errors}
                        />
                    )}
                    {step === 3 && <Step3 values={values} setFieldValue={setFieldValue} />}
                    {step === 4 && <Step4 formData={values} />}
                    {step === 5 && <Step5 formData={values} />}

                    {/* Add this error message display */}
                    {registrationError && (
                        <div className="invalid-feedback" style={{
                            display: 'block',
                            textAlign: 'center',
                            marginTop: '20px',
                            marginBottom: '20px',
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                            padding: '15px',
                            borderRadius: '5px',
                            border: '1px solid #f5c6cb'
                        }}>
                            <Icon icon="f7:exclamationmark-circle-fill" style={{marginRight:'8px'}}/>
                            {registrationError}
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '0 8px' }}>
                        {step > 1 && (
                            <button
                                type="button"
                                className='button button-transparent-black'
                                onClick={handlePrev}
                                style={{maxWidth:'150px'}}
                            >
                                Back
                            </button>
                        )}
                        <button
                            type="submit"
                            className='button'
                            style={{ maxWidth: '160px', marginLeft: 'auto' }}
                            disabled={isCheckingEmail || isSubmitting}
                        >
                            {isCheckingEmail ? 'Checking email...' :
                             step < 5 ? 'Next' :
                             isSubmitting ? 'Creating Account...' : 'Submit'}
                        </button>
                    </div>
                </Form>
            )}

        </Formik>
    </>
    );
};

export default RegisterForm2;
